import { useIntersectionObserver } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import { useInfiniteEmployeeHistoricalActivityTimelineQuery } from "@components/Cards/HistoricalActivityTimelineCard/HistoricalActivityTimelineCard.g";

import { Card } from "@design-system/Card/Card";
import { Icon } from "@design-system/Icon/Icon";
import { Logo } from "@design-system/Logo/Logo";
import { ScrollBox } from "@design-system/ScrollBox/ScrollBox";
import { Spinner } from "@design-system/Spinner/Spinner";
import { Text } from "@design-system/Text/Text";

import classes from "./HistoricalActivityTimelineCard.module.css";

type Props = {
  clientId: string;
  clientEmployeeId: string;
  dateRange: [Date | null, Date | null];
};

export const HistoricalActivityTimelineCard = ({
  clientId,
  clientEmployeeId,
  dateRange,
}: Props) => {
  const startDate = dateRange[0]?.toISOString();
  const endDate = dateRange[1]?.toISOString();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteEmployeeHistoricalActivityTimelineQuery(
      {
        clientId,
        clientEmployeeId,
        first: 20,
        startDate,
        endDate,
      },
      {
        initialPageParam: { after: null as string | null },
        getNextPageParam: (lastPage) => {
          const pageInfo =
            lastPage.clientEmployee?.historicalActivities?.pageInfo;
          if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return undefined;
          return { after: pageInfo.endCursor };
        },
        refetchOnWindowFocus: false,
      },
    );

  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: "100px",
  });

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const events = useMemo(() => {
    const rawEvents =
      data?.pages.flatMap((page) =>
        page.clientEmployee?.historicalActivities?.edges?.map(
          (edge) => edge?.node,
        ),
      ) ?? [];

    return rawEvents.filter(
      (event): event is NonNullable<typeof event> => event != null,
    );
  }, [data]);

  const groupedEvents = useMemo(() => {
    const groups: { date: string; events: NonNullable<(typeof events)[0]>[] }[] = [];
    let currentDate = "";

    events.forEach((event) => {
      const date = dayjs(event.timestamp).format("MMM D, YYYY");
      if (date !== currentDate) {
        currentDate = date;
        groups.push({ date, events: [] });
      }
      groups[groups.length - 1].events.push(event);
    });

    return groups;
  }, [events]);

  const [foldedDates, setFoldedDates] = useState<Record<string, boolean>>({});

  const toggleFold = (date: string) => {
    setFoldedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  const totalCount =
    data?.pages[0]?.clientEmployee?.historicalActivities?.totalCount ?? 0;

  const renderIcon = (event: NonNullable<(typeof events)[0]>) => {
    if (event.application?.resource?.publicResource?.signedLogoUrl) {
      return (
        <Logo
          src={event.application.resource.publicResource.signedLogoUrl}
          size="sm"
        />
      );
    }
    
    if (event.eventType === "CREDENTIAL_USE") {
      return <Icon icon="faKey" className={classes.icon} />;
    }
    if (event.eventType === "NOTIFICATION") {
      return <Icon icon="faBell" className={classes.icon} />;
    }
    return <Icon icon="faClock" className={classes.icon} />;
  };

  return (
    <Card stretch grow>
      <Card.Header>
        <Card.Header.Title>Historical Activity</Card.Header.Title>
        <Card.Header.Actions>
          <Text size={12} color="textSecondary">
            {totalCount} events
          </Text>
        </Card.Header.Actions>
      </Card.Header>
      <Card.Body grow noOverflow>
        <ScrollBox stretch>
          <div className={classes.container}>
            {isLoading ? (
              <div className={classes.loading}>
                <Spinner />
              </div>
            ) : events.length === 0 ? (
              <div className={classes.empty}>
                <Text color="textSecondary">No historical activity found for this period.</Text>
              </div>
            ) : (
              <div className={classes.timeline}>
                {groupedEvents.map((group) => {
                  const isFolded = foldedDates[group.date];
                  return (
                    <div key={group.date} className={classes.dateGroup}>
                      <div
                        className={classes.dateSeparator}
                        onClick={() => toggleFold(group.date)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className={classes.dateSeparatorText}>{group.date}</div>
                      </div>
                      {!isFolded ? (
                        group.events.map((event) => (
                          <div key={event?.id} className={classes.event}>
                            <div className={classes.iconContainer}>
                              {renderIcon(event)}
                            </div>
                            <div className={classes.content}>
                              <div className={classes.header}>
                                <Text size={14} weight={600} color="textDark">
                                  {event?.application?.resource?.publicResource?.productName || "System Event"}
                                </Text>
                                <div className={classes.timestamp}>
                                  {dayjs(event?.timestamp).format("h:mm A")}
                                </div>
                              </div>
                              <div className={classes.description}>
                                {event?.description}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          className={classes.foldedIndicator}
                          onClick={() => toggleFold(group.date)}
                          role="button"
                          tabIndex={0}
                        >
                          <div className={classes.iconContainer}>
                            {renderIcon(group.events[0])}
                          </div>
                          <div className={classes.content}>
                            <div className={classes.unfoldText}>
                              Click to unfold {group.events.length} activities
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {hasNextPage && (
                  <div ref={ref} className={classes.loading}>
                    <Spinner size={24} />
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollBox>
      </Card.Body>
    </Card>
  );
};